import { type DeleteDependency } from "../../components/ConfigListShell/ConfigDeleteDialog";
import { IAnalyte } from "../../domain/analyte.interface";

/**
 * SIMS-8853 — what the analyte delete confirmation says, kept out of the container so it can be
 * tested without mounting a screen. The cup analogue is `cupDelete.ts`.
 */

/** The design identifies an analyte by shortname and scientific name (markup L1471). */
export const analyteDeleteName = (analyte: IAnalyte): string =>
	`${analyte.shortname} — ${analyte.scientificName}`;

/**
 * The design's own remedy, not the dialog's default "Set it to Inactive instead". For an analyte
 * that default would be wrong: going inactive does not lift the block — only taking it off the
 * cups does.
 */
export const analyteDeleteBlockedSuggestion =
	"Remove it from those panels before deleting.";

export const analyteDeleteAllowedBody = (analyte: IAnalyte): string =>
	`${analyteDeleteName(analyte)} will be permanently deleted. This cannot be undone. It is not on any cup.`;

/** AC1 — one dependency, the cups carrying it. The dialog singularises "1 cup". */
export const analyteDeleteDependencies = (cups: number): DeleteDependency[] =>
	cups > 0 ? [{ kind: "cups", count: cups, label: "cups" }] : [];

/**
 * The cup count a DELETE was refused with (SIMS-8853 backend guard).
 *
 * The dialog's count came from the cups list and can be stale by the time Delete is clicked, so
 * the server's number replaces it. Reads the unwrapped body first — `analyteService.deleteAnalyte`
 * rejects with `err.response?.data || err` — and the axios shape too, for a caller that has not
 * unwrapped. Any other 409, or one without a positive count, is not a carriage refusal.
 */
export const cupsFromConflict = (error: unknown): DeleteDependency[] | null => {
	const e = error as {
		statusCode?: number;
		cups?: unknown;
		response?: { status?: number; data?: { cups?: unknown } };
	};
	const status = e?.statusCode ?? e?.response?.status;
	if (status !== 409) {
		return null;
	}
	const cups = e?.response?.data?.cups ?? e?.cups;
	if (typeof cups !== "number" || cups <= 0) {
		return null;
	}
	return analyteDeleteDependencies(cups);
};
